/**
 * SpeakerCheck - Speaker/audio output verification
 * Nordic Sanctuary design: Interactive test, clear feedback
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Volume2, VolumeX, Play, CheckCircle, AlertTriangle, ArrowRight } from "lucide-react";
import { Button } from "@jourfx/ui";

interface SpeakerCheckProps {
  onResult: (passed: boolean) => void;
  onContinue: () => void;
}

export function SpeakerCheck({ onResult, onContinue }: SpeakerCheckProps) {
  const { t } = useTranslation("techcheck");
  const [status, setStatus] = useState<"idle" | "playing" | "pass" | "warning">("idle");
  const [isPlaying, setIsPlaying] = useState(false);

  const playTestSound = () => {
    const audioContext = new AudioContext();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 440;
    gainNode.gain.value = 0.3;

    setIsPlaying(true);
    setStatus("playing");
    oscillator.start();

    setTimeout(() => {
      oscillator.stop();
      setIsPlaying(false);
    }, 1500);
  };

  const handleHeard = () => {
    setStatus("pass");
    onResult(true);
  };

  const handleNotHeard = () => {
    setStatus("warning");
    onResult(false);
  };

  return (
    <div className="space-y-6">
      {/* Speaker indicator */}
      <div className="flex flex-col items-center py-6">
        <div
          className={`
            w-24 h-24 rounded-3xl flex items-center justify-center transition-all duration-300
            ${
              status === "pass"
                ? "bg-[var(--sage-500)] shadow-lg shadow-[var(--sage-300)]/30"
                : status === "warning"
                  ? "bg-[oklch(0.92_0.08_80)]"
                  : "bg-[var(--sage-100)]"
            }
          `}
        >
          {status === "warning" ? (
            <VolumeX className="w-10 h-10 text-[oklch(0.55_0.15_80)]" aria-hidden="true" />
          ) : (
            <Volume2
              className={`w-10 h-10 ${status === "pass" ? "text-white" : "text-[var(--sage-600)]"} ${isPlaying ? "animate-pulse" : ""}`}
              aria-hidden="true"
            />
          )}
        </div>

        {/* Idle state - play button */}
        {status === "idle" && (
          <Button variant="secondary" size="client" className="mt-6" onClick={playTestSound}>
            <Play aria-hidden="true" />
            {t("checks.speaker.testButton")}
          </Button>
        )}

        {/* Playing state - confirmation buttons */}
        {status === "playing" && (
          <div className="mt-6 space-y-4 text-center">
            <p className="text-[var(--stone-700)] font-medium">{t("checks.speaker.heardQuestion")}</p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" size="client" onClick={handleNotHeard}>
                {t("checks.speaker.heardNo")}
              </Button>
              <Button size="client" onClick={handleHeard}>
                {t("checks.speaker.heardYes")}
              </Button>
            </div>
          </div>
        )}

        {/* Pass state */}
        {status === "pass" && (
          <div className="mt-4 flex items-center gap-2 text-[var(--sage-600)]">
            <CheckCircle className="w-5 h-5" aria-hidden="true" />
            <span className="font-semibold">{t("checks.speaker.success")}</span>
          </div>
        )}

        {/* Warning state */}
        {status === "warning" && (
          <div className="mt-4 text-center">
            <div className="flex items-center justify-center gap-2 text-[oklch(0.55_0.15_80)]">
              <AlertTriangle className="w-5 h-5" aria-hidden="true" />
              <span className="font-semibold">{t("checks.speaker.problemDetected")}</span>
            </div>
            <p className="text-sm text-[var(--stone-500)] mt-2 max-w-sm">
              {t("checks.speaker.checkVolume")}
            </p>
            <Button variant="secondary" size="client" className="mt-4" onClick={playTestSound}>
              {t("actions.retry")}
            </Button>
          </div>
        )}
      </div>

      {/* Continue button */}
      {(status === "pass" || status === "warning") && (
        <Button size="client" className="w-full" onClick={onContinue}>
          {t("actions.continue")}
          <ArrowRight aria-hidden="true" />
        </Button>
      )}
    </div>
  );
}
